import React, { useState } from 'react';
import { MapPin, ChevronLeft, ChevronRight, School, Building2 } from 'lucide-react';
import { schoolsByParish, educationStats, PARISHES } from '../data/education-data';

const BackButton = ({ goBack, label = "Back" }) => (
  <button onClick={goBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 font-medium">
    <ChevronLeft className="w-5 h-5" />{label}
  </button>
);

// Parish colors for visual variety
const parishColors = {
  'Kingston': 'from-red-500 to-red-600',
  'St. Andrew': 'from-blue-500 to-blue-600',
  'St. Thomas': 'from-brand-500 to-brand-600',
  'Portland': 'from-brand-500 to-brand-600',
  'St. Mary': 'from-brand-500 to-brand-600',
  'St. Ann': 'from-cyan-500 to-cyan-600',
  'Trelawny': 'from-sky-500 to-sky-600',
  'St. James': 'from-indigo-500 to-indigo-600',
  'Hanover': 'from-violet-500 to-violet-600',
  'Westmoreland': 'from-purple-500 to-purple-600',
  'St. Elizabeth': 'from-fuchsia-500 to-fuchsia-600',
  'Manchester': 'from-pink-500 to-pink-600',
  'Clarendon': 'from-orange-500 to-orange-600',
  'St. Catherine': 'from-amber-500 to-amber-600'
};

export const SchoolsByParishPage = ({ setPage, goBack }) => {
  const [selectedParish, setSelectedParish] = useState(null);

  // Get school count for each parish
  const parishData = PARISHES.map(parish => ({
    name: parish,
    schools: schoolsByParish[parish] || [],
    count: (schoolsByParish[parish] || []).length
  })).sort((a, b) => b.count - a.count);

  if (selectedParish) {
    const schools = schoolsByParish[selectedParish] || [];

    return (
      <div className="w-full">
        <button
          onClick={() => setSelectedParish(null)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 font-medium"
        >
          <ChevronLeft className="w-5 h-5" />Back to Parishes
        </button>

        <div className={`bg-gradient-to-br ${parishColors[selectedParish] || 'from-gray-500 to-gray-600'} rounded-2xl p-8 text-white mb-8`}>
          <MapPin className="w-12 h-12 mb-4" />
          <h1 className="text-3xl font-black mb-2">{selectedParish}</h1>
          <p className="text-white/80">{schools.length} secondary schools</p>
        </div>

        <div className="space-y-3">
          {schools.map((school, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex items-center gap-4"
            >
              <div className="w-10 h-10 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0">
                <School className="w-5 h-5 text-brand-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-800">{school.school_name}</h3>
                <div className="flex gap-2 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    school.sector === 'Public' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                  }`}>
                    {school.sector}
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                    {school.school_type}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick action to filter in main directory */}
        <div className="mt-6">
          <button
            onClick={() => setPage('schools')}
            className="w-full p-4 bg-brand-50 border border-brand-200 rounded-xl text-brand-700 font-medium hover:bg-brand-100 transition-colors"
          >
            View in Full Directory with Filters
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      {/* Header */}
      <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
        <MapPin className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-black mb-2">Schools by Parish</h1>
        <p className="text-brand-100">Browse {educationStats.totalSchools} schools across Jamaica's 14 parishes</p>
      </div>

      {/* Parish Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {parishData.map((parish) => (
          <button
            key={parish.name}
            onClick={() => setSelectedParish(parish.name)}
            className={`bg-gradient-to-br ${parishColors[parish.name] || 'from-gray-500 to-gray-600'} rounded-xl p-4 text-white text-left hover:scale-105 transition-transform shadow-md`}
          >
            <div className="flex items-center justify-between mb-2">
              <MapPin className="w-5 h-5 opacity-80" />
              <ChevronRight className="w-4 h-4 opacity-60" />
            </div>
            <h3 className="font-bold text-lg leading-tight">{parish.name}</h3>
            <p className="text-sm opacity-80 mt-1">{parish.count} schools</p>
          </button>
        ))}
      </div>

      {/* Summary Stats */}
      <div className="mt-8 grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100">
          <div className="text-2xl font-black text-brand-600">{educationStats.totalSchools}</div>
          <div className="text-sm text-gray-600">Total Schools</div>
        </div>
        <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100">
          <div className="text-2xl font-black text-blue-600">{educationStats.totalPublicSchools}</div>
          <div className="text-sm text-gray-600">Public</div>
        </div>
        <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100">
          <div className="text-2xl font-black text-purple-600">{educationStats.totalIndependentSchools}</div>
          <div className="text-sm text-gray-600">Independent</div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="mt-6 flex gap-4">
        <button
          onClick={() => setPage('schools')}
          className="flex-1 p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-brand-300 transition-all text-left flex items-center gap-3"
        >
          <Building2 className="w-6 h-6 text-brand-600" />
          <div>
            <div className="font-semibold text-gray-800">Full Directory</div>
            <div className="text-xs text-gray-500">Search all schools</div>
          </div>
        </button>
        <button
          onClick={() => setPage('compare-schools')}
          className="flex-1 p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-blue-300 transition-all text-left flex items-center gap-3"
        >
          <School className="w-6 h-6 text-blue-600" />
          <div>
            <div className="font-semibold text-gray-800">Compare</div>
            <div className="text-xs text-gray-500">Side by side</div>
          </div>
        </button>
      </div>
    </div>
  );
};

export default SchoolsByParishPage;
